// Single owner of the admin session lifecycle outside React.
//
// Responsibilities:
// - keep the current access token in memory (and mirrored to storage);
// - attach it to every private request through ONE interceptor pair that is
//   registered once (components used to register their own pair each mount);
// - refresh at most once at a time ("single flight"), coordinated across tabs
//   with the Web Locks API when available, because the server rotates refresh
//   tokens and revokes the whole family when a token is reused;
// - bound retries: a 401 retries the original request exactly once after a
//   successful refresh (the backend only answers 401 from its `authenticate`
//   preHandler, i.e. before any handler ran, so replaying is safe);
// - never treat a 403 (permission denied), a network failure, or a 5xx as an
//   expired session;
// - terminate an invalid session cleanly without loops, and ignore a refresh
//   result that resolves after logout or after a different account signed in.

import axiosPublic, { axiosPrivate } from "../API/axios";
import {
  clearStoredAuthSession,
  getStoredPersistPreference,
  isAccessTokenUsable,
  persistAuthSessionState,
  readStoredAuthSession,
  revokeStoredRefreshToken,
  SESSION_STORAGE_KEYS,
} from "../utils/session";

export const SESSION_EVENT = Object.freeze({
  AUTHENTICATED: "authenticated",
  REFRESHED: "refreshed",
  EXPIRED: "expired",
  LOGGED_OUT: "logged_out",
});

// Why a session ended, for the login page (`reason` of the EXPIRED event).
export const SESSION_END_REASON = Object.freeze({
  ACCOUNT_SUSPENDED: "account_suspended",
  SESSION_EXPIRED: "session_expired",
});

// Server codes that end the session outright (DESIGN §1/§8): refreshing
// cannot help, so the client never refreshes or replays on them.
// - 401 ACCOUNT_SUSPENDED (authenticate) / 403 ACCOUNT_SUSPENDED (refresh):
//   the user or the agency was suspended;
// - 401 SESSION_EXPIRED: the session reached its absolute maximum duration.
const TERMINAL_CODE_REASONS = Object.freeze({
  ACCOUNT_SUSPENDED: SESSION_END_REASON.ACCOUNT_SUSPENDED,
  SESSION_EXPIRED: SESSION_END_REASON.SESSION_EXPIRED,
});
const terminalReasonOf = (error) => TERMINAL_CODE_REASONS[error?.response?.data?.code];

export class SessionExpiredError extends Error {
  constructor(message = "Session expired", cause, reason) {
    super(message);
    this.name = "SessionExpiredError";
    this.sessionExpired = true;
    this.cause = cause;
    this.reason = reason;
  }
}

export class SessionChangedError extends Error {
  constructor(message = "Session changed during request") {
    super(message);
    this.name = "SessionChangedError";
    this.sessionChanged = true;
  }
}

const REFRESH_LOCK_NAME = "bluewing-admin-session-refresh";
const REFRESH_PATH = "/auth/refresh-token";
// Delays before retrying a refresh with an unknown outcome. Both retries land
// well inside the server's fixed 30 s recovery window.
export const REFRESH_RETRY_DELAYS_MS = Object.freeze([1000, 3000]);
const wait = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

// Methods whose replay after a refresh is safe for any 401. Other methods are
// replayed only for the authenticate preHandler's TOKEN_EXPIRED response,
// which is sent before any route handler runs (verified in
// src/startup/startServer.ts sendUnauthorized), so a mutation is never
// replayed after a handler-level outcome.
const SAFE_RETRY_METHODS = new Set(["get", "head", "options"]);
const isReplayable401 = (config, error) => {
  const method = String(config?.method || "get").toLowerCase();
  if (SAFE_RETRY_METHODS.has(method)) {
    return true;
  }
  return error?.response?.data?.code === "TOKEN_EXPIRED";
};

const state = {
  session: readStoredAuthSession(),
  generation: 0,
  refreshPromise: null,
  listeners: new Set(),
  installed: false,
};

const notify = (event, payload) => {
  state.listeners.forEach((listener) => {
    try {
      listener(event, payload);
    } catch {
      // A listener failure must never break the session machinery.
    }
  });
};

export const subscribeToSession = (listener) => {
  state.listeners.add(listener);
  return () => state.listeners.delete(listener);
};

export const getSessionSnapshot = () => state.session;

export const getAccessToken = () => state.session?.accessToken || "";

export const hasStoredSession = () =>
  Boolean(state.session?.accessToken || state.session?.refreshToken);

export const getSessionGeneration = () => state.generation;

const isAuthFailureStatus = (status) =>
  status === 401 || status === 403 || status === 404;

const applySession = (nextSession, { persist, event }) => {
  state.session = { ...nextSession };
  persistAuthSessionState(state.session, { persist });
  notify(event, state.session);
};

// Called by the login page after a successful /auth/login.
export const startSession = (sessionState, { persist } = {}) => {
  state.generation += 1;
  state.refreshPromise = null;
  applySession(sessionState, {
    persist: persist ?? getStoredPersistPreference(),
    event: SESSION_EVENT.AUTHENTICATED,
  });
};

// Called after /user/update-password, which returns a fresh token pair.
export const replaceTokens = (tokens, { persist } = {}) => {
  if (!tokens?.accessToken || !tokens?.refreshToken) {
    return;
  }
  applySession(
    {
      ...state.session,
      accessToken: tokens.accessToken,
      accessTokenExpiresAt: 0,
      refreshToken: tokens.refreshToken,
    },
    { persist: persist ?? getStoredPersistPreference(), event: SESSION_EVENT.REFRESHED },
  );
};

const terminateSession = (event, reason) => {
  state.generation += 1;
  state.refreshPromise = null;
  clearStoredAuthSession();
  state.session = readStoredAuthSession();
  notify(event, { reason });
};

// Local + best-effort remote logout. A refresh that is still in flight when
// this runs resolves into a stale generation and is discarded.
export const endSession = async ({ revoke = true } = {}) => {
  const generationAtStart = state.generation;
  if (revoke) {
    await revokeStoredRefreshToken(axiosPublic);
  }
  if (generationAtStart !== state.generation) {
    return;
  }
  terminateSession(SESSION_EVENT.LOGGED_OUT, "user");
};

const performRefresh = async (generationAtStart, staleAccessToken) => {
  const run = async () => {
    // Another tab may have rotated the tokens while we waited for the lock.
    // Compare with the token that was stale when this refresh was requested,
    // not with in-memory state: the storage-event sync may already have
    // copied the other tab's tokens into memory, and rotating them again
    // would be a needless second rotation.
    const stored = readStoredAuthSession();
    if (
      stored.accessToken &&
      stored.accessToken !== staleAccessToken &&
      isAccessTokenUsable(stored.accessToken, stored.accessTokenExpiresAt)
    ) {
      state.session = { ...stored };
      notify(SESSION_EVENT.REFRESHED, state.session);
      return stored.accessToken;
    }

    const refreshToken = stored.refreshToken || state.session?.refreshToken;
    if (!refreshToken) {
      throw new SessionExpiredError("Missing refresh token");
    }

    // Bounded retry for a refresh whose outcome is unknown (no response, or a
    // 5xx). The server may already have rotated the token; it keeps the
    // issued successor recoverable for a fixed 30 s, so retrying the SAME
    // token shortly afterwards returns that successor instead of tripping
    // reuse detection. At most REFRESH_RETRY_DELAYS_MS.length retries, then
    // the error surfaces and the session is kept (no loop).
    let response;
    for (let attempt = 0; ; attempt += 1) {
      try {
        response = await axiosPublic.post(REFRESH_PATH, {
          refresh_token: refreshToken,
        });
        break;
      } catch (error) {
        const status = Number(error?.response?.status || 0);
        if (generationAtStart !== state.generation) {
          throw new SessionChangedError();
        }
        if (isAuthFailureStatus(status)) {
          const reason = terminalReasonOf(error) || `refresh_${status}`;
          terminateSession(SESSION_EVENT.EXPIRED, reason);
          throw new SessionExpiredError("Refresh token rejected", error, reason);
        }
        const outcomeUnknown = !error?.response || status >= 500;
        if (!outcomeUnknown || attempt >= REFRESH_RETRY_DELAYS_MS.length) {
          // Keep the session, surface the error.
          throw error;
        }
        await wait(REFRESH_RETRY_DELAYS_MS[attempt]);
        if (generationAtStart !== state.generation) {
          throw new SessionChangedError();
        }
      }
    }

    if (generationAtStart !== state.generation) {
      throw new SessionChangedError();
    }

    const tokens = response?.data?.data?.tokens || {};
    if (!tokens.accessToken || !tokens.refreshToken) {
      terminateSession(SESSION_EVENT.EXPIRED, "refresh_malformed");
      throw new SessionExpiredError("Malformed refresh response");
    }

    applySession(
      {
        ...state.session,
        ...readStoredAuthSession(),
        accessToken: tokens.accessToken,
        accessTokenExpiresAt: 0,
        refreshToken: tokens.refreshToken,
      },
      { persist: getStoredPersistPreference(), event: SESSION_EVENT.REFRESHED },
    );
    return tokens.accessToken;
  };

  const locks = typeof navigator !== "undefined" ? navigator.locks : undefined;
  if (locks?.request) {
    return locks.request(REFRESH_LOCK_NAME, run);
  }
  return run();
};

// Single-flight refresh: concurrent callers share one promise.
export const refreshSession = () => {
  if (state.refreshPromise) {
    return state.refreshPromise;
  }
  const generationAtStart = state.generation;
  const staleAccessToken = state.session?.accessToken || "";
  const promise = performRefresh(generationAtStart, staleAccessToken).finally(() => {
    if (state.refreshPromise === promise) {
      state.refreshPromise = null;
    }
  });
  state.refreshPromise = promise;
  return promise;
};

// Returns a usable access token, refreshing first when necessary.
export const ensureAccessToken = async () => {
  const { accessToken, accessTokenExpiresAt, refreshToken } = state.session || {};
  if (accessToken && isAccessTokenUsable(accessToken, accessTokenExpiresAt)) {
    return accessToken;
  }
  if (!refreshToken) {
    throw new SessionExpiredError("No session");
  }
  return refreshSession();
};

// Restore the session on a full page load: uses the stored access token when
// still valid, otherwise spends one refresh. Never redirects by itself.
export const bootstrapSession = async () => {
  state.session = readStoredAuthSession();
  if (!hasStoredSession()) {
    return { authenticated: false };
  }
  try {
    await ensureAccessToken();
    return { authenticated: true, session: state.session };
  } catch (error) {
    if (error?.sessionExpired) {
      return { authenticated: false, expired: true, reason: error.reason };
    }
    // Backend unreachable: keep the stored session and let pages show their
    // own error states instead of bouncing the user to the login screen.
    return { authenticated: Boolean(state.session?.refreshToken), degraded: true, session: state.session };
  }
};

const setAuthorizationHeader = (config, token) => {
  if (!config.headers) {
    config.headers = {};
  }
  if (typeof config.headers.set === "function") {
    config.headers.set("Authorization", `Bearer ${token}`);
  } else {
    config.headers.Authorization = `Bearer ${token}`;
  }
};

const hasAuthorizationHeader = (config) => {
  const headers = config?.headers;
  if (!headers) return false;
  const value =
    typeof headers.get === "function" ? headers.get("Authorization") : headers.Authorization;
  return Boolean(value);
};

const isRefreshRequest = (config) =>
  String(config?.url || "").includes(REFRESH_PATH);

// Installs the request/response interceptors exactly once.
export const installAuthInterceptors = () => {
  if (state.installed) {
    return;
  }
  state.installed = true;

  axiosPrivate.interceptors.request.use(async (config) => {
    if (hasAuthorizationHeader(config)) {
      return config;
    }
    config.sessionGeneration = state.generation;
    try {
      const token = await ensureAccessToken();
      setAuthorizationHeader(config, token);
    } catch (error) {
      if (error?.sessionExpired || error?.sessionChanged) {
        throw error;
      }
      // Refresh failed for a non-auth reason: send what we have so the
      // request fails with the real server/network error.
      if (state.session?.accessToken) {
        setAuthorizationHeader(config, state.session.accessToken);
      }
    }
    return config;
  });

  axiosPrivate.interceptors.response.use(
    (response) => response,
    async (error) => {
      const config = error?.config;
      const status = Number(error?.response?.status || 0);

      if (status !== 401 || !config || isRefreshRequest(config)) {
        return Promise.reject(error);
      }

      const belongsToCurrentSession =
        config.sessionGeneration === undefined
          ? hasStoredSession()
          : config.sessionGeneration === state.generation;

      // BW D10: a suspended account or an expired absolute session ends the
      // session for ANY request, mutations included (they are never
      // replayed). Without this the operator stayed "signed in" until a later
      // GET happened to trigger a refresh.
      const terminalReason = terminalReasonOf(error);
      if (terminalReason) {
        if (belongsToCurrentSession) {
          terminateSession(SESSION_EVENT.EXPIRED, terminalReason);
        }
        error.sessionEnded = true;
        return Promise.reject(error);
      }

      if (config.authRetried) {
        return Promise.reject(error);
      }

      if (!isReplayable401(config, error)) {
        // Not replayed (it may not be safe), but the session is checked with
        // one shared refresh: a revoked session (e.g. token version bumped by
        // a password or role change) ends here instead of lingering.
        config.authRetried = true;
        if (belongsToCurrentSession) {
          try {
            await refreshSession();
          } catch (refreshError) {
            if (refreshError?.sessionExpired) {
              return Promise.reject(refreshError);
            }
          }
        }
        return Promise.reject(error);
      }

      if (config.sessionGeneration !== undefined && config.sessionGeneration !== state.generation) {
        return Promise.reject(new SessionChangedError());
      }

      config.authRetried = true;
      let token;
      try {
        token = await refreshSession();
      } catch (refreshError) {
        if (refreshError?.sessionExpired || refreshError?.sessionChanged) {
          return Promise.reject(refreshError);
        }
        error.refreshError = refreshError;
        return Promise.reject(error);
      }

      setAuthorizationHeader(config, token);
      return axiosPrivate(config);
    },
  );
};

// Cross-tab coordination: when another tab logs out, this tab follows; when
// another tab rotates tokens, this tab adopts them instead of refreshing.
export const installStorageSync = () => {
  if (typeof window === "undefined" || state.storageSyncInstalled) {
    return;
  }
  state.storageSyncInstalled = true;
  window.addEventListener("storage", (event) => {
    if (!event || event.storageArea !== window.localStorage) {
      return;
    }
    if (event.key === null || event.key === SESSION_STORAGE_KEYS.refreshToken) {
      const stored = readStoredAuthSession();
      if (!stored.refreshToken && hasStoredSession()) {
        terminateSession(SESSION_EVENT.LOGGED_OUT, "other_tab");
        return;
      }
    }
    if (
      event.key === SESSION_STORAGE_KEYS.accessToken ||
      event.key === SESSION_STORAGE_KEYS.refreshToken
    ) {
      const stored = readStoredAuthSession();
      if (stored.refreshToken && stored.refreshToken !== state.session?.refreshToken) {
        state.session = { ...stored };
        notify(SESSION_EVENT.REFRESHED, state.session);
      }
    }
  });
};

// Test hook: reset module state between tests.
export const __resetAuthClientForTests = () => {
  state.session = readStoredAuthSession();
  state.generation = 0;
  state.refreshPromise = null;
  state.listeners.clear();
};
